import { createFileRoute, notFound } from '@tanstack/react-router';
import { PageBody } from '@wundo/ui';
import { NotFoundPage } from '@/components/NotFoundPage.tsx';
import { PageTitle } from '@/components/PageTitle.tsx';
import { loadOrNotFound } from '@/lib/loadOrNotFound.ts';
import { roleplaysQuery } from '@/lib/roleplaysQuery.ts';
import { m } from '@/paraglide/messages.js';
import { PracticePage } from './index.tsx';

/** What the address can ask on arrival. */
interface PracticeRoleplaySearch {
  /** Start a practice of this conversation, as its start button would. */
  start?: boolean;
}

/**
 * The practice page with one conversation's card open.
 *
 * This is the address a mail or a message can point at: the reader lands on
 * the same list as everybody else, with the named card unfolded so the
 * situation and the button are in front of them. Only the "try a roleplay"
 * card in Teams adds `start`: its button already was the reader's click on
 * start, so the practice opens over the page straight away.
 */
export const Route = createFileRoute('/_app/practice/$roleplayId/')({
  validateSearch: (search: Record<string, unknown>): PracticeRoleplaySearch => ({
    start: ['1', 'true'].includes(String(search.start)) ? true : undefined,
  }),
  /* A card the reader cannot see is as gone as one that was deleted. */
  loader: ({ context, params }) =>
    loadOrNotFound(async () => {
      const list = await context.queryClient.ensureQueryData(roleplaysQuery());
      const cards = [...list.library, ...list.personal];
      if (!cards.some((card) => card.id === params.roleplayId)) throw notFound();
    }),
  /* Shown straight away, as the page's own loading line was before the loader. */
  pendingComponent: PracticeRoleplayPending,
  pendingMs: 0,
  pendingMinMs: 0,
  notFoundComponent: NotFoundPage,
  component: PracticeRoleplayRoute,
});

function PracticeRoleplayPending() {
  return (
    <>
      <PageTitle>{m.nav_practice()}</PageTitle>
      <PageBody>
        <p className='text-base text-ink-muted'>{m.practice_loading()}</p>
      </PageBody>
    </>
  );
}

function PracticeRoleplayRoute() {
  const { roleplayId } = Route.useParams();
  const { start } = Route.useSearch();
  return (
    <PracticePage
      openRoleplayId={roleplayId}
      startRoleplayId={start ? roleplayId : undefined}
    />
  );
}
